/* eslint-disable react-refresh/only-export-components */

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert, { type AlertColor } from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

export type AppSnackbarSeverity = AlertColor;

export interface AppSnackbarOptions {
  message: React.ReactNode;
  severity?: AppSnackbarSeverity;
  durationMs?: number;
  action?: React.ReactNode;
  /**
   * Prevent showing the same message repeatedly.
   * Defaults to true.
   */
  dedupe?: boolean;
}

interface AppSnackbarItem extends Required<Pick<AppSnackbarOptions, "dedupe">> {
  id: number;
  message: React.ReactNode;
  severity: AppSnackbarSeverity;
  durationMs: number;
  action?: React.ReactNode;
}

interface AppSnackbarContextValue {
  enqueueSnackbar: (opts: AppSnackbarOptions) => void;
}

const AppSnackbarContext = createContext<AppSnackbarContextValue | null>(null);

const DEFAULT_DURATION_MS = 6000;

export function AppSnackbarProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const nextIdRef = useRef(1);
  const lastMessageKeyRef = useRef<string | null>(null);

  const [, setQueue] = useState<AppSnackbarItem[]>([]);
  const [current, setCurrent] = useState<AppSnackbarItem | null>(null);
  const [open, setOpen] = useState(false);

  const pushNext = useCallback(() => {
    setQueue((prev) => {
      const [next, ...rest] = prev;
      if (next) {
        setCurrent(next);
        setOpen(true);
        lastMessageKeyRef.current =
          typeof next.message === "string" ? next.message : null;
      } else {
        setCurrent(null);
      }
      return rest;
    });
  }, []);

  const enqueueSnackbar = useCallback(
    (opts: AppSnackbarOptions) => {
      const item: AppSnackbarItem = {
        id: nextIdRef.current++,
        message: opts.message,
        severity: opts.severity ?? "info",
        durationMs: opts.durationMs ?? DEFAULT_DURATION_MS,
        action: opts.action,
        dedupe: opts.dedupe ?? true,
      };

      if (
        item.dedupe &&
        typeof item.message === "string" &&
        item.message.length > 0 &&
        item.message === lastMessageKeyRef.current
      ) {
        return;
      }

      if (!current) {
        setCurrent(item);
        setOpen(true);
        lastMessageKeyRef.current =
          typeof item.message === "string" ? item.message : null;
        return;
      }

      setQueue((prev) => [...prev, item]);
    },
    [current],
  );

  const handleClose = useCallback((_event?: unknown, reason?: string) => {
    // Keep snack visible if user clicks away.
    if (reason === "clickaway") return;
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    pushNext();
  }, [pushNext]);

  return (
    <AppSnackbarContext.Provider value={{ enqueueSnackbar }}>
      {children}
      <Snackbar
        key={current?.id ?? "empty"}
        open={open}
        onClose={handleClose}
        autoHideDuration={current?.durationMs}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "& .MuiSnackbarContent-root": { p: 0, background: "transparent" },
          mb: { xs: 2, sm: 3 },
        }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            variant="filled"
            elevation={6}
            action={
              current.action ?? (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => setOpen(false)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{
              alignItems: "center",
              borderRadius: 2,
              minWidth: { xs: "min(92vw, 520px)", sm: 520 },
            }}
          >
            {current.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </AppSnackbarContext.Provider>
  );
}

export function useAppSnackbar() {
  const ctx = useContext(AppSnackbarContext);
  if (!ctx) {
    throw new Error("useAppSnackbar must be used within AppSnackbarProvider");
  }
  return ctx;
}
